import { useState, useRef } from "react";
import { AxisBottom, AxisLeft } from "@visx/axis"
import { LinePath, Circle, Line } from '@visx/shape';
import { scaleLinear, scaleOrdinal } from "@visx/scale";
import { GridRows, GridColumns } from "@visx/grid";
import { extent } from "d3-array";
import { motion } from "framer-motion"
import { alphabet, computerNumberStyles, getTooltipProportionText, tooltipTextStyles } from "../../assets/constants";
import PlotTooltip from "../plot-tooltip/plot-tooltip.component";
import { localPoint } from '@visx/event';
import HoverRect from "../hover-rect/hover-rect.component";
import { Space, Divider, Flex, Text } from "@mantine/core";
import { curveMonotoneX } from "d3-shape";
import { schemeTableau10 } from "d3-scale-chromatic";
import LogoWithText from "../logo-with-text/logo-with-text.component";
import SvgPlotWrapper from "../svg-plot-wrapper/svg-plot-wrapper.component";

const makeLine = (lineData, answer, xScale, yScale, colorScale, duration) => {
    return (
        <LinePath
            key={`${answer}-line-path`}
            data={lineData}
            x={d => xScale(d.year) ?? 0}
            y={d => yScale(d.responses[answer]) ?? 0}
            curve={curveMonotoneX}
        >
            {
                ({ path }) => (
                    <motion.path
                        key={`${answer}-path`}
                        stroke={colorScale(answer)}
                        fill='none'
                        strokeWidth='3'
                        shapeRendering="geometricPrecision"
                        opacity='0.8'
                        initial={{ pathLength: 0, }}
                        animate={{ pathLength: 1, d: path(lineData) }}
                        transition={{ duration }}
                    />
                )
            }
        </LinePath>
    )
}

const makeLinePlotTextLabel = (answer, index, plotLetters, yScale, colorScale, firstYearData, lastYearData, dimensions, duration) => {
    return (
        <motion.text
            key={`${answer}-label`}
            width={dimensions.margins.right - 5}
            fill={colorScale(answer)}
            dominantBaseline='middle'
            // style={{ fontSize: '28px' }}
            initial={{ x: 5, y: yScale(firstYearData.responses[answer]) }}
            animate={{ x: dimensions.innerWidth + 5, y: yScale(lastYearData.responses[answer]) }}
            transition={{ duration }}
            opacity={1}
        >
            {plotLetters ? alphabet[index] : answer}
        </motion.text>
    )
}

const tooltipMargin = 5;

const LinePlot = ({
    dimensions,
    surveyResponse,
    isSmall,
    isMedium,
    animateTransition,
    hasMouse,
    plotType,
    hideAxisLabels = false,
}) => {

    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltipOffset, setTooltipOffset] = useState({});
    const [hoverData, setHoverdata] = useState(null);

    const uniqueYears = [...new Set(surveyResponse.surveyData.map(d => d.year))];

    const animationDuration = animateTransition ? 0.4 : 0;

    const yExtent = extent(surveyResponse.surveyData.map(d => Object.values(d.responses)).flat());
    const yDomainMin = yExtent[0] < 0.9 ? 0 : yExtent[0] - 0.05;
    const yDomainMax = yExtent[1] > 0.9 ? 1 : yExtent[1] + 0.05;
    const domain = [yDomainMin, yDomainMax];
    const yScale = scaleLinear({
        domain,
        range: [dimensions.innerHeight, 0]
    })
    const xScale = scaleLinear({
        domain: surveyResponse.surveyYears,
        range: [0, dimensions.innerWidth]
    });

    const colorScale = scaleOrdinal({
        domain: surveyResponse.surveyAnswers,
        range: schemeTableau10
    })

    const firstYeardata = surveyResponse.surveyData.filter(d => d.year === surveyResponse.surveyYears[0])[0];
    const lastYearData = surveyResponse.surveyData.filter(d => d.year === surveyResponse.surveyYears[1])[0];
    const longestAnswer = Math.max(...(surveyResponse.surveyAnswers.map(answer => answer.length)));
    const plotLetters = longestAnswer > 9 // || (innerHeight < 600 || isSmall);

    const yTickTranslateAmount = isSmall ? -8 : 0;

    // Tooltip
    const tooltipRef = useRef(null);
    const tooltipHeight = tooltipRef?.current?.offsetHeight || 0;
    const tooltipWidth = tooltipRef?.current?.offsetWidth || 0;

    const showTooltipFunction = (event) => {
        const { x, y } = localPoint(event) || { x: 0 };
        // Draw a circle on every line at the x position
        const xValue = xScale.invert(x - dimensions.margins.left);
        // Get closest value from uniqueYears
        let closestYear = uniqueYears.reduce((prev, curr) => Math.abs(curr - xValue) < Math.abs(prev - xValue) ? curr : prev);
        closestYear = Math.min(closestYear, surveyResponse.surveyYears[1]);
        closestYear = Math.max(closestYear, surveyResponse.surveyYears[0]);
        const closestYearData = surveyResponse.surveyData.filter(d => d.year === closestYear)[0];
        const newHoverData = { year: closestYear, data: closestYearData.responses };
        setHoverdata(newHoverData);
        setTooltipOpen(true);
        const yValForTooltip = y < dimensions.height / 2 ? y + tooltipMargin : y - tooltipHeight - tooltipMargin;
        const xScaled = xScale(closestYear);
        const xValForTooltip = xScaled < dimensions.innerWidth / 2 ? xScaled + dimensions.margins.left + tooltipMargin : xScaled + dimensions.margins.left - (tooltipMargin + tooltipWidth);
        setTooltipOffset({ x: xValForTooltip, y: yValForTooltip });
    };

    const hideTooltipFunction = () => {
        setTooltipOpen(false);
        setHoverdata(null);
    };

    const yAxisLabel = 'Proportion';
    
    const numYears = surveyResponse.surveyYears[1] - surveyResponse.surveyYears[0] + 1;

    const divisorForYAxis = numYears > 10 ? 2 : 1;

    return (
        <>
            <SvgPlotWrapper dimensions={dimensions}>
                <GridRows scale={yScale} width={dimensions.innerWidth} height={dimensions.innerHeight} stroke='rgba(255,255,255,0.8)' />
                <GridColumns scale={xScale} width={dimensions.innerWidth} height={dimensions.innerHeight} stroke='rgba(255,255,255,0.8)' />
                <LogoWithText innerWidth={dimensions.innerWidth} innerHeight={dimensions.innerHeight} />
                {surveyResponse.surveyAnswers.map(answer => makeLine(surveyResponse.surveyData, answer, xScale, yScale, colorScale, animationDuration))}
                {plotType === 'estimated' && 
                    surveyResponse.surveyAnswers.map((answer, index) => {
                        return makeLinePlotTextLabel(answer, index, plotLetters, yScale, colorScale, firstYeardata, lastYearData, dimensions, animationDuration)
                    })}
                <AxisLeft
                    scale={yScale}
                    label={(isMedium === false && hideAxisLabels === false) && yAxisLabel}
                    labelOffset={40}
                    numTicks={isSmall ? 0 : 10}
                    hideAxisLine={isSmall}
                    tickLabelProps={() => ({
                        fontSize: 13,
                        textAnchor: 'end',
                        dominantBaseline: 'middle',
                        transform: 'translate(-2,0)'
                    })}
                    labelProps={{ style: { fontSize: 16, textAnchor: 'middle', fontWeight: '400' } }}
                />
                <AxisBottom
                    top={dimensions.innerHeight}
                    scale={xScale}
                    tickFormat={d => String(d)}
                    hideTicks={isSmall}
                    tickValues={xScale.ticks().filter(d => d % divisorForYAxis === 0)}
                    label={(dimensions.width >= 500 && hideAxisLabels === false) && 'Year'}
                    labelOffset={23}
                    tickLabelProps={(d, i) => ({
                        transform: `translate(0,${yTickTranslateAmount})`,
                        fontSize: 13,
                        textAnchor: 'middle'
                    })}
                    labelProps={{ style: { fontSize: 16, textAnchor: 'middle', fontWeight: '400' } }}
                />
                <HoverRect
                    innerWidth={dimensions.innerWidth}
                    innerHeight={dimensions.innerHeight}
                    hasMouse={hasMouse}
                    showFunction={showTooltipFunction}
                    hideFunction={hideTooltipFunction}
                />
                {
                    hoverData &&
                    <>
                        <Line
                            from={{ x: xScale(hoverData.year), y: yScale(0) }}
                            to={{ x: xScale(hoverData.year), y: yDomainMax }}
                            stroke='black'
                            opacity={0.5}
                            strokeWidth={1}
                            strokeDasharray="2,1"
                            style={{ pointerEvents: 'none' }}
                        />
                        {
                            surveyResponse.surveyAnswers.map((answer, index) => (
                                <Circle
                                    key={`circle-${index}`}
                                    cx={xScale(hoverData.year)}
                                    cy={yScale(hoverData.data[answer])}
                                    r={5}
                                    fill={colorScale(answer)}
                                    stroke={colorScale(answer)}
                                    strokeWidth={12}
                                    strokeOpacity={0.25}
                                    opacity = {0.8}
                                    style={{ pointerEvents: 'none' }}
                                />
                            ))
                        }
                    </>
                }
            </SvgPlotWrapper>
            {
                tooltipOpen &&
                <PlotTooltip
                    tooltipOffset={tooltipOffset}
                    ref={tooltipRef}
                    plotWidth={dimensions.innerWidth}
                >
                    <Divider 
                        label = {hoverData.year}
                        labelPosition="center" 
                        labelProps={{ size: isSmall ? 'xs' : 'sm', weight: 500 }}
                    />
                    {
                        surveyResponse.surveyAnswers.map((answer, index) => (
                            <Flex key={`tooltip-text-${index}`} justify='end' align='center' wrap="nowrap">
                                <Text style={tooltipTextStyles} size={isSmall ? 'xs' : 'sm'}>{answer}</Text>
                                <Text size={isSmall ? 'xs' : 'sm'}>:&nbsp;</Text>
                                <Text style={computerNumberStyles} size={isSmall ? 'xs' : 'sm'}>{getTooltipProportionText(hoverData.data[answer],plotType)}</Text>
                                <Space w={5} />
                                <Text size='xl' color={colorScale(answer)} style={{ lineHeight: '0px' }}>•</Text>
                            </Flex>
                        ))
                    }
                </PlotTooltip>
            }
        </>
    )

}

export default LinePlot;